/**
 * Display helpers for card partner data, shared by the member's My Cards block
 * (CardPartnerDataComponent) and the admin pages. Values arrive in the partner's
 * vocabulary; these map them to labels (i18n keys under `mycards.partner.*`),
 * badge colours and number formats. The partner's name is never shown anywhere.
 */

// Proper nouns — not translated. Addresses use the long network names, the
// deposit/withdrawal feed uses short chain codes (the partner's two vocabularies).
const NETWORK_LABELS: Record<string, string> = {
  BNB_SMART_CHAIN: 'BNB Smart Chain', ETHEREUM: 'Ethereum', TRON: 'Tron', SOLANA: 'Solana',
  ARBITRUM: 'Arbitrum', AVALANCHE: 'Avalanche', POLYGON: 'Polygon', OPTIMISM: 'Optimism', TON: 'TON'
};
const CHAIN_LABELS: Record<string, string> = {
  BNB: 'BNB Smart Chain', BSC: 'BNB Smart Chain', ETH: 'Ethereum', TRON: 'Tron', TRX: 'Tron', SOL: 'Solana',
  ARB: 'Arbitrum', AVAX: 'Avalanche', MATIC: 'Polygon', POL: 'Polygon', OP: 'Optimism', TON: 'TON'
};

// Values we have labels for; anything else is shown as the partner sent it.
export const CARD_PARTNER_KNOWN_VALUES: Record<string, string[]> = {
  account: ['active', 'pending_kyc', 'blocked'],
  kyc: ['NOT_STARTED', 'IN_PROGRESS', 'PENDING', 'PENDING_CARDHOLDER', 'RESUBMIT', 'APPROVED', 'FAILED', 'CONTACT_SUPPORT'],
  card: ['Active', 'Frozen', 'Inactive', 'Blocked'],
  physical: ['PREORDERED', 'PREORDERED_WITH_DELIVERY_SUBMITTED', 'DISTRIBUTED'],
  type: ['TransferIn', 'Consumption', 'Fee_Consumption'],
  tx_status: ['Closed', 'Pending', 'Fail'],
  transfer_status: ['COMPLETE', 'CREDITED', 'PENDING', 'REFUNDED', 'FAILED']
};

export const CARD_PARTNER_KYC_STATUSES = CARD_PARTNER_KNOWN_VALUES['kyc'];

/** i18n key for a known partner value, else the raw value (the pipe passes unknown strings through). */
export function cardPartnerKeyOr(kind: string, value: string | null | undefined): string {
  if (!value) return '—';
  return CARD_PARTNER_KNOWN_VALUES[kind]?.includes(value) ? `mycards.partner.${kind}_${value}` : value;
}

export function cardPartnerNetworkLabel(network: string): string {
  return NETWORK_LABELS[network] ?? network;
}

export function cardPartnerChainLabel(chain: string | null | undefined): string {
  if (!chain) return '—';
  return CHAIN_LABELS[chain.toUpperCase()] ?? chain;
}

export function cardPartnerKycColor(status: string | null | undefined): string {
  switch (status) {
    case 'APPROVED': return 'success';
    case 'IN_PROGRESS':
    case 'PENDING':
    case 'PENDING_CARDHOLDER': return 'warning';
    case 'RESUBMIT':
    case 'FAILED':
    case 'CONTACT_SUPPORT': return 'danger';
    default: return 'secondary';
  }
}

export function cardPartnerAccountColor(status: string | null | undefined): string {
  switch (status) {
    case 'active': return 'success';
    case 'pending_kyc': return 'warning';
    case 'blocked': return 'danger';
    default: return 'secondary';
  }
}

export function cardPartnerCardColor(status: string | null | undefined): string {
  const s = (status ?? '').toLowerCase();
  if (s === 'active') return 'success';
  if (s === 'frozen') return 'info';
  return s ? 'secondary' : 'light';
}

export function cardPartnerStatusColor(status: string | null | undefined): string {
  const s = (status ?? '').toLowerCase();
  if (['closed', 'complete', 'credited'].includes(s)) return 'success';
  if (['pending'].includes(s)) return 'warning';
  if (['fail', 'failed'].includes(s)) return 'danger';
  return 'secondary';
}

/** Minimal shape of a card transaction for sign/amount rendering. */
export interface CardPartnerTxLike {
  type: string | null;
  amount: string | null;
  fee: string | null;
  currency: string | null;
}

/**
 * Amounts arrive positive for both directions, so the sign comes from the type:
 * a load onto the card is money in, a purchase or a fee is money out.
 */
export function cardPartnerTxDirection(t: CardPartnerTxLike): 'in' | 'out' | 'none' {
  if (t.type === 'TransferIn') return 'in';
  if (t.type === 'Consumption' || (t.type ?? '').startsWith('Fee_')) return 'out';
  return 'none';
}

/** Signed billing amount; a fee row carries its value in `fee` with amount 0. */
export function cardPartnerTxAmount(t: CardPartnerTxLike, locale: string): string {
  let value = Number(t.amount ?? 0);
  if ((t.type ?? '').startsWith('Fee_') && value === 0) value = Number(t.fee ?? 0);
  const dir = cardPartnerTxDirection(t);
  const sign = dir === 'in' ? '+' : dir === 'out' ? '−' : '';
  return `${sign}${cardPartnerMoney(value, locale)} ${t.currency ?? ''}`.trim();
}

export function cardPartnerMoney(value: number | string | null | undefined, locale: string): string {
  const n = Number(value ?? 0);
  return (isFinite(n) ? n : 0).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Crypto amounts: keep up to 8 decimals, drop trailing zeros. */
export function cardPartnerCrypto(value: string | null | undefined, locale: string): string {
  if (value == null || value === '') return '—';
  return Number(value).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 8 });
}

export function cardPartnerShortHash(hash: string | null | undefined): string {
  if (!hash) return '—';
  return hash.length > 16 ? `${hash.slice(0, 8)}…${hash.slice(-6)}` : hash;
}

/** Copies text to the clipboard, with a textarea fallback for older WebViews. */
export function cardPartnerCopy(value: string): Promise<boolean> {
  const fallback = (): boolean => {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    document.body.removeChild(textarea);
    return ok;
  };
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(value).then(() => true).catch(() => fallback());
  }
  return Promise.resolve(fallback());
}
